import {
  CacheLookupPolicy,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { logger } from "../logger-init";
import { buildB2CAuthorityMetadata } from "./b2cAuthorityMetadata";

/**
 * MSAL Configuration for Azure AD B2C
 *
 * This configuration supports:
 * - Azure AD B2C multi-policy flows (signin, resetpassword)
 * - Token caching in sessionStorage/sessionStorage
 * - Comprehensive logging for debugging
 * - MSAL Browser v4 async initialization pattern
 */

interface B2CConfig {
  instance: string;
  tenant: string;
  /**
   * Directory (GUID) id of the B2C tenant — the issuer in the openid-configuration
   * document. Optional: when absent MSAL falls back to network discovery.
   */
  tenantId?: string;
  clientId: string;
  cacheLocation: "sessionStorage" | "sessionStorage";
  scopes: string[];
  policies: {
    signin: string;
    resetpassword: string;
  };
  redirectUri: string;
  postLogoutRedirectUri: string;
}

/**
 * Default scopes required for all authentication flows
 */
const defaultScopes = ["openid"];

/** True when a support engineer has flipped MSAL verbose logging on in this browser. */
function isMsalDebugEnabled(): boolean {
  try {
    return window.localStorage.getItem("msal.debug") === "1";
  } catch {
    return false;
  }
}

/**
 * Creates MSAL configuration from B2C settings
 * @param config B2C configuration object
 * @returns MSAL Configuration object
 */
function createMsalConfig(config: B2CConfig): Configuration {
  // Merge custom scopes with default scopes (deduplicated)
  const loginScopes = Array.from(new Set([...config.scopes, ...defaultScopes]));

  // Static discovery document so MSAL skips the openid-configuration fetch on
  // every page load (see b2cAuthorityMetadata.ts). Null → network discovery.
  const authorityMetadata = buildB2CAuthorityMetadata(config.instance, config.tenantId);

  return {
    auth: {
      authority: config.instance,
      clientId: config.clientId,
      redirectUri: config.redirectUri,
      knownAuthorities: Object.values(config.policies).map((policy) => {
        return config.instance.replace(config.policies.signin, policy);
      }),
      ...(authorityMetadata ? { authorityMetadata: JSON.stringify(authorityMetadata) } : {}),
      navigateToLoginRequestUrl: false,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: config.cacheLocation,
      storeAuthStateInCookie: true,
      secureCookies: window.location.protocol === "https:",
    },
    system: {
      // Bounds for MSAL's HIDDEN-IFRAME silent renewal (the path acquireTokenSilent
      // falls back to when it needs a fresh B2C round-trip rather than a cached
      // refresh token). MSAL's recommended values; the defaults let an iframe
      // redirect against B2C time out as `monitor_window_timeout`, which surfaced
      // in skyNote as users being silently signed out after an idle tab.
      iframeHashTimeout: 10000,
      windowHashTimeout: 60000,
      loadFrameTimeout: 10000,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              logger.error(message);
              return;
            case LogLevel.Info:
              logger.info(message);
              return;
            case LogLevel.Verbose:
              logger.debug(message);
              return;
            case LogLevel.Warning:
              logger.warn(message);
              return;
          }
        },
        // Field diagnosis switch: `localStorage.setItem("msal.debug", "1")` in an
        // affected browser turns on MSAL's own verbose trace (cache lookups,
        // redirect handling, token response processing) without a redeploy.
        logLevel: isMsalDebugEnabled() ? LogLevel.Verbose : LogLevel.Warning,
      },
    },
  };
}

/**
 * Initialize B2C configuration from environment variables
 */
function initializeB2CConfig(): B2CConfig {
  return {
    instance: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
    tenant: import.meta.env.VITE_B2C_CONFIG_TENANT,
    tenantId: import.meta.env.VITE_B2C_CONFIG_TENANT_ID || undefined,
    clientId: import.meta.env.VITE_B2C_CONFIG_CLIENT_ID,
    cacheLocation: import.meta.env.VITE_B2C_CONFIG_CACHE_LOCATION as
      | "sessionStorage"
      | "sessionStorage",
    scopes: JSON.parse(import.meta.env.VITE_B2C_CONFIG_SCOPES || "[]"),
    policies: JSON.parse(import.meta.env.VITE_B2C_CONFIG_POLICIES || "{}"),
    redirectUri: window.location.origin + "/callback",
    postLogoutRedirectUri: window.location.href,
  };
}

/**
 * Create and initialize MSAL instance
 *
 * IMPORTANT: MSAL Browser v4 requires async initialization
 * This must be called before any authentication operations
 *
 * @returns Initialized PublicClientApplication instance
 */
export async function createMsalInstance(): Promise<PublicClientApplication> {
  const b2cConfig = initializeB2CConfig();

  // Log B2C configuration for debugging (without sensitive values)
  logger.debug("B2C Configuration", {
    instance: b2cConfig.instance,
    tenant: b2cConfig.tenant,
    policies: b2cConfig.policies,
    redirectUri: b2cConfig.redirectUri,
  });

  const msalConfig = createMsalConfig(b2cConfig);

  // Log derived MSAL configuration
  logger.debug("MSAL Configuration", {
    authority: msalConfig.auth?.authority,
    knownAuthorities: msalConfig.auth?.knownAuthorities,
  });

  const msalInstance = new PublicClientApplication(msalConfig);

  // MSAL v4 Breaking Change: initialize() must be called and awaited
  await msalInstance.initialize();

  // NOTE: Do NOT call handleRedirectPromise() here.
  // MsalProvider calls it internally — a double call leaves MSAL's
  // in-memory interaction state conflicted, causing interaction_in_progress
  // errors when LoginHandler attempts loginRedirect.

  return msalInstance;
}

/**
 * Get login scopes (including default scopes)
 */
function getLoginScopes(b2cConfig: B2CConfig): string[] {
  return Array.from(new Set([...b2cConfig.scopes, ...defaultScopes]));
}

/**
 * Export singleton B2C config for use across the app
 */
export const b2cConfig = initializeB2CConfig();

/**
 * Export policies for B2C flows (signin, resetpassword)
 * Used by useB2CPolicies hook for policy-specific redirect handling
 */
export const policies = b2cConfig.policies;

/**
 * Export login scopes for use in authentication flows
 */
export const loginScopes = getLoginScopes(b2cConfig);

/**
 * Login request configuration for redirect flows
 * Used by ProtectedRoute and other authentication flows
 */
export const loginRequest = {
  scopes: loginScopes,
};

/**
 * Policy for every acquireTokenSilent call: serve from the cache or the refresh
 * token only — never MSAL's hidden-iframe fallback. Under the default policy an
 * expired or missing refresh token is routed into that iframe, which against B2C
 * (a different site, so its cookie is third-party) hangs until the timeout and
 * then reports a TRANSIENT error, masking the one signal that matters: a dead
 * session looked like a network blip. With this policy the same condition fails
 * at once with the terminal interaction-required code, and the callers' existing
 * acquireTokenRedirect fallback is the recovery.
 */
export const silentTokenPolicy = {
  cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
} as const;
